import React, { useState, useEffect } from 'react';
import { 
  Users, Search, Edit2, DollarSign, TrendingUp, 
  Download, Building2, Clock, MapPin, Briefcase, CheckCircle2
} from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Separator } from '../../ui/separator';
import { db } from '../../../lib/db';
import { formatUSDCurrency, PAY_FREQUENCIES } from '../../../lib/usPayrollCalculations';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

const US_STATES = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
  'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
  'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
  'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
  'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
  'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
};

export default function SalaryStructures() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [compensation, setCompensation] = useState([]);
  const [payGroups, setPayGroups] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [payTypeFilter, setPayTypeFilter] = useState('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState({
    payType: 'salary',
    payRate: '',
    workState: '',
    residentState: '',
    flsaStatus: 'exempt',
    payGroupId: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersData, compData, groupsData] = await Promise.all([
        db.users.toArray(),
        db.employeeCompensation.toArray(),
        db.payGroups.toArray()
      ]);
      setUsers(usersData.filter(u => u.status === 'active' && u.role !== 'admin'));
      setCompensation(compData);
      setPayGroups(groupsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const departments = [...new Set(users.map(u => u.profile?.department).filter(Boolean))];

  const getCompensation = (userId) => {
    return compensation.find(c => c.userId === userId);
  };

  const filteredUsers = users.filter(user => {
    const comp = getCompensation(user.id);
    const matchesSearch = 
      user.profile?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.profile?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDepartment = departmentFilter === 'all' || user.profile?.department === departmentFilter;
    const matchesPayType = payTypeFilter === 'all' || comp?.payType === payTypeFilter;
    return matchesSearch && matchesDepartment && matchesPayType;
  });

  const handleEditCompensation = async (user) => {
    const comp = getCompensation(user.id);
    
    // Fetch W4 data to get work state if not in compensation
    let w4Data = null;
    try {
      w4Data = await db.employeeW4.where('userId').equals(user.id).first();
    } catch (e) {
      console.warn('Could not fetch W4 data:', e);
    }
    
    // Priority for work state: compensation > W4 > profile
    const workState = comp?.workState || w4Data?.workState || user.profile?.workState || '';
    const residentState = comp?.residentState || w4Data?.residentState || user.profile?.residentState || workState;
    
    setSelectedEmployee(user);
    setFormData({
      payType: comp?.payType || 'salary',
      payRate: comp?.payRate || '',
      workState: workState,
      residentState: residentState,
      flsaStatus: comp?.flsaStatus || 'exempt',
      payGroupId: comp?.payGroupId || payGroups[0]?.id || '',
      // Overtime settings
      standardHoursPerDay: comp?.standardHoursPerDay || 8,
      standardHoursPerWeek: comp?.standardHoursPerWeek || 40,
      overtimeMultiplier: comp?.overtimeMultiplier || 1.5,
      doubleTimeMultiplier: comp?.doubleTimeMultiplier || 2.0,
      weeklyOvertimeThreshold: comp?.weeklyOvertimeThreshold || 40,
      dailyOvertimeThreshold: comp?.dailyOvertimeThreshold || 8,
      // Track if data was pre-filled from onboarding
      _prefilled: {
        workState: !!(w4Data?.workState || user.profile?.workState),
        residentState: !!(w4Data?.residentState || user.profile?.residentState)
      }
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!formData.payRate || parseFloat(formData.payRate) <= 0) {
      toast.error('Pay rate is required and must be greater than 0');
      return;
    }
    if (!formData.workState) {
      toast.error('Work state is required');
      return;
    }

    try {
      const existingComp = getCompensation(selectedEmployee.id);
      const selectedPayGroup = payGroups.find(g => g.id === formData.payGroupId);
      const compData = {
        userId: selectedEmployee.id,
        payType: formData.payType,
        payRate: parseFloat(formData.payRate),
        payFrequency: selectedPayGroup?.frequency || 'biweekly',
        workState: formData.workState,
        residentState: formData.residentState || formData.workState,
        flsaStatus: formData.payType === 'hourly' ? 'nonexempt' : formData.flsaStatus,
        payGroupId: formData.payGroupId,
        // Overtime settings
        standardHoursPerDay: parseFloat(formData.standardHoursPerDay) || 8,
        standardHoursPerWeek: parseFloat(formData.standardHoursPerWeek) || 40,
        overtimeMultiplier: parseFloat(formData.overtimeMultiplier) || 1.5,
        doubleTimeMultiplier: parseFloat(formData.doubleTimeMultiplier) || 2.0,
        weeklyOvertimeThreshold: parseFloat(formData.weeklyOvertimeThreshold) || 40,
        dailyOvertimeThreshold: parseFloat(formData.dailyOvertimeThreshold) || 8,
        effectiveDate: Date.now(),
        status: 'active',
        updatedAt: Date.now()
      };

      if (existingComp) {
        await db.employeeCompensation.update(existingComp.id, compData);
        toast.success('Compensation updated successfully');
      } else {
        await db.employeeCompensation.add({
          id: uuidv4(),
          ...compData,
          createdAt: Date.now()
        });
        toast.success('Compensation created successfully');
      }

      setShowEditModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving compensation:', error);
      toast.error('Failed to save compensation');
    }
  };

  const calculateAnnualSalary = (comp) => {
    if (!comp) return 0;
    if (comp.payType === 'salary') {
      return comp.payRate;
    } else {
      // Hourly: assume 2080 hours/year (40 hrs * 52 weeks)
      return comp.payRate * 2080;
    }
  };

  const calculatePerPeriodPay = (comp) => {
    if (!comp) return 0;
    const periodsPerYear = PAY_FREQUENCIES[comp.payFrequency]?.periodsPerYear || 26;
    if (comp.payType === 'salary') {
      return comp.payRate / periodsPerYear;
    } else {
      // Hourly: standard hours per period
      const hoursPerPeriod = {
        'weekly': 40,
        'biweekly': 80,
        'semimonthly': 86.67,
        'monthly': 173.33
      };
      return comp.payRate * (hoursPerPeriod[comp.payFrequency] || 80);
    }
  };

  const handleExport = () => {
    if (filteredUsers.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const exportData = filteredUsers.map(user => {
      const comp = getCompensation(user.id);
      const payGroup = comp?.payGroupId ? payGroups.find(g => g.id === comp.payGroupId) : null;
      return {
        'Employee ID': user.id.slice(0, 8),
        'Name': `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim(),
        'Email': user.email || '',
        'Department': user.profile?.department || '',
        'Position': user.profile?.position || '',
        'Pay Type': comp?.payType === 'hourly' ? 'Hourly' : 'Salary',
        'Pay Rate': comp?.payRate || 0,
        'Pay Group': payGroup?.name || '',
        'Pay Frequency': payGroup?.frequency || '',
        'Annual Salary': calculateAnnualSalary(comp),
        'Work State': comp?.workState || '',
        'Resident State': comp?.residentState || '',
        'FLSA Status': comp?.flsaStatus || '',
        'Status': comp ? 'Configured' : 'Not Set'
      };
    });

    const headers = Object.keys(exportData[0] || {});
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => headers.map(h => {
        const val = row[h];
        if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `us_compensation_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    
    toast.success(`Exported ${exportData.length} compensation records`);
  };

  const totalAnnualPayroll = compensation.reduce((sum, c) => sum + calculateAnnualSalary(c), 0);
  const configuredCount = compensation.filter(c => users.some(u => u.id === c.userId)).length;
  const hourlyCount = compensation.filter(c => c.payType === 'hourly').length;
  const salaryCount = compensation.filter(c => c.payType === 'salary').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employee Compensation</h1>
          <p className="text-gray-500 mt-1">Manage US employee pay rates and compensation</p>
        </div>
        <Button variant="outline" onClick={handleExport}>
          <Download className="w-4 h-4 mr-2" /> Export
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Employees</p>
                <p className="text-2xl font-bold text-gray-900">{users.length}</p>
                <p className="text-xs text-gray-400 mt-1">{configuredCount} configured</p>
              </div>
              <Users className="w-8 h-8 text-teal-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Annual Payroll</p>
                <p className="text-2xl font-bold text-gray-900">{formatUSDCurrency(totalAnnualPayroll)}</p>
                <p className="text-xs text-gray-400 mt-1">{formatUSDCurrency(totalAnnualPayroll / 12)}/mo</p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Salaried</p>
                <p className="text-2xl font-bold text-gray-900">{salaryCount}</p>
                <p className="text-xs text-gray-400 mt-1">Exempt employees</p>
              </div>
              <Briefcase className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Hourly</p>
                <p className="text-2xl font-bold text-gray-900">{hourlyCount}</p>
                <p className="text-xs text-gray-400 mt-1">Non-exempt employees</p>
              </div>
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={payTypeFilter} onValueChange={setPayTypeFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Pay Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="salary">Salaried</SelectItem>
                <SelectItem value="hourly">Hourly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Employee Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Employee</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Pay Type</TableHead>
                <TableHead className="text-right">Pay Rate</TableHead>
                <TableHead className="text-right">Annual Salary</TableHead>
                <TableHead>Work State</TableHead>
                <TableHead>Pay Group</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map(user => {
                const comp = getCompensation(user.id);
                const annualSalary = calculateAnnualSalary(comp);
                
                return (
                  <TableRow key={user.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-medium">
                          {user.profile?.firstName?.[0]}{user.profile?.lastName?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {user.profile?.firstName} {user.profile?.lastName}
                          </p>
                          <p className="text-xs text-gray-500">{user.profile?.position || 'N/A'}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="bg-gray-100">
                        {user.profile?.department || 'N/A'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {comp ? (
                        <Badge className={comp.payType === 'hourly' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>
                          {comp.payType === 'hourly' ? 'Hourly' : 'Salary'}
                        </Badge>
                      ) : (
                        <Badge variant="outline">Not Set</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {comp ? (
                        comp.payType === 'hourly' 
                          ? `${formatUSDCurrency(comp.payRate)}/hr`
                          : formatUSDCurrency(comp.payRate)
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-teal-600">
                      {comp ? formatUSDCurrency(annualSalary) : '-'}
                    </TableCell>
                    <TableCell>
                      {comp?.workState ? (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-gray-400" />
                          <span className="text-sm">{comp.workState}</span>
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {comp?.payGroupId ? (
                        <span className="text-sm">{payGroups.find(g => g.id === comp.payGroupId)?.name || '-'}</span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEditCompensation(user)}
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        {comp ? 'Edit' : 'Set'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Compensation Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedEmployee && `${getCompensation(selectedEmployee.id) ? 'Edit' : 'Set'} Compensation - ${selectedEmployee?.profile?.firstName} ${selectedEmployee?.profile?.lastName}`}
            </DialogTitle>
            <DialogDescription>
              Configure US payroll compensation for this employee
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Pay Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Pay Type *</Label>
                <Select 
                  value={formData.payType} 
                  onValueChange={(v) => setFormData({ 
                    ...formData, 
                    payType: v,
                    flsaStatus: v === 'hourly' ? 'nonexempt' : 'exempt'
                  })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="salary">Salary (Annual)</SelectItem>
                    <SelectItem value="hourly">Hourly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="payRate">
                  {formData.payType === 'hourly' ? 'Hourly Rate *' : 'Annual Salary *'}
                </Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="payRate"
                    type="number"
                    value={formData.payRate}
                    onChange={(e) => setFormData({ ...formData, payRate: e.target.value })}
                    className="pl-10"
                    placeholder={formData.payType === 'hourly' ? '35.00' : '75000'}
                    step={formData.payType === 'hourly' ? '0.01' : '1000'}
                  />
                </div>
              </div>
            </div>

            {/* FLSA Status */}
            <div>
              <Label>FLSA Status</Label>
              <Select 
                value={formData.flsaStatus} 
                onValueChange={(v) => setFormData({ ...formData, flsaStatus: v })}
                disabled={formData.payType === 'hourly'}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="exempt">Exempt</SelectItem>
                  <SelectItem value="nonexempt">Non-Exempt</SelectItem>
                </SelectContent>
              </Select>
              {formData.payType === 'hourly' && (
                <p className="text-xs text-gray-500 mt-1">Hourly employees are automatically Non-Exempt</p>
              )}
            </div>

            {/* Work State & Resident State */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <Label>Work State *</Label>
                  {formData._prefilled?.workState && formData.workState && (
                    <span className="flex items-center gap-1 text-xs text-green-600">
                      <CheckCircle2 className="w-3 h-3" />
                      From onboarding
                    </span>
                  )}
                </div>
                <Select 
                  value={formData.workState} 
                  onValueChange={(v) => setFormData({ 
                    ...formData, 
                    workState: v,
                    residentState: formData.residentState || v
                  })}
                >
                  <SelectTrigger className={`mt-1 ${formData._prefilled?.workState && formData.workState ? 'border-green-200 bg-green-50' : ''}`}>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(US_STATES).map(([code, name]) => (
                      <SelectItem key={code} value={code}>{code} - {name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Label>Resident State</Label>
                  {formData._prefilled?.residentState && formData.residentState && (
                    <span className="flex items-center gap-1 text-xs text-green-600">
                      <CheckCircle2 className="w-3 h-3" />
                      From onboarding
                    </span>
                  )}
                </div>
                <Select 
                  value={formData.residentState} 
                  onValueChange={(v) => setFormData({ ...formData, residentState: v })}
                >
                  <SelectTrigger className={`mt-1 ${formData._prefilled?.residentState && formData.residentState ? 'border-green-200 bg-green-50' : ''}`}>
                    <SelectValue placeholder="Same as work" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(US_STATES).map(([code, name]) => (
                      <SelectItem key={code} value={code}>{code} - {name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Pay Group */}
            <div>
              <Label>Pay Group</Label>
              <Select 
                value={formData.payGroupId} 
                onValueChange={(v) => setFormData({ ...formData, payGroupId: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select pay group" />
                </SelectTrigger>
                <SelectContent>
                  {payGroups.map(group => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name} ({group.frequency})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Overtime Settings - Only for hourly/non-exempt employees */}
            {(formData.payType === 'hourly' || formData.flsaStatus === 'nonexempt') && (
              <>
                <Separator className="my-2" />
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-500" />
                    Overtime Settings
                  </h4>
                  <p className="text-xs text-gray-500">Configure overtime thresholds and multipliers for this employee</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Standard Hours/Day</Label>
                      <Input
                        type="number"
                        min="1"
                        max="12"
                        step="0.5"
                        value={formData.standardHoursPerDay}
                        onChange={(e) => setFormData({ ...formData, standardHoursPerDay: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Weekly OT Threshold</Label>
                      <Input
                        type="number"
                        min="20"
                        max="60"
                        value={formData.weeklyOvertimeThreshold}
                        onChange={(e) => setFormData({ ...formData, weeklyOvertimeThreshold: e.target.value })}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Hours/week before OT kicks in</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Overtime Multiplier</Label>
                      <Select 
                        value={formData.overtimeMultiplier?.toString()} 
                        onValueChange={(v) => setFormData({ ...formData, overtimeMultiplier: v })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1.25">1.25x (Time & Quarter)</SelectItem>
                          <SelectItem value="1.5">1.5x (Time & Half)</SelectItem>
                          <SelectItem value="1.75">1.75x</SelectItem>
                          <SelectItem value="2">2x (Double Time)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Double Time Multiplier</Label>
                      <Select 
                        value={formData.doubleTimeMultiplier?.toString()} 
                        onValueChange={(v) => setFormData({ ...formData, doubleTimeMultiplier: v })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="2">2x</SelectItem>
                          <SelectItem value="2.5">2.5x</SelectItem>
                          <SelectItem value="3">3x (Triple Time)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">For hours over 12/day or holidays</p>
                    </div>
                  </div>

                  {/* Overtime Pay Preview */}
                  {formData.payType === 'hourly' && parseFloat(formData.payRate) > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                      <p className="text-sm font-medium text-amber-900 mb-2">Overtime Pay Rates</p>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <span className="text-gray-600">Regular:</span>
                          <span className="ml-1 font-medium">${parseFloat(formData.payRate).toFixed(2)}/hr</span>
                        </div>
                        <div>
                          <span className="text-amber-600">OT ({formData.overtimeMultiplier}x):</span>
                          <span className="ml-1 font-medium">${(parseFloat(formData.payRate) * parseFloat(formData.overtimeMultiplier)).toFixed(2)}/hr</span>
                        </div>
                        <div>
                          <span className="text-red-600">DT ({formData.doubleTimeMultiplier}x):</span>
                          <span className="ml-1 font-medium">${(parseFloat(formData.payRate) * parseFloat(formData.doubleTimeMultiplier)).toFixed(2)}/hr</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Summary */}
            <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-lg p-4 mt-2">
              {(() => {
                const selectedPayGroup = payGroups.find(g => g.id === formData.payGroupId);
                const payFrequency = selectedPayGroup?.frequency || 'biweekly';
                const hoursPerPeriod = { weekly: 40, biweekly: 80, semimonthly: 86.67, monthly: 173.33 };
                return (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-600">
                        {formData.payType === 'hourly' ? 'Estimated Annual (2080 hrs)' : 'Annual Salary'}
                      </span>
                      <span className="text-xl font-bold text-teal-600">
                        {formatUSDCurrency(
                          formData.payType === 'hourly' 
                            ? (parseFloat(formData.payRate) || 0) * 2080
                            : parseFloat(formData.payRate) || 0
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Per Pay Period ({payFrequency})</span>
                      <span className="font-medium text-gray-700">
                        {formatUSDCurrency(
                          formData.payType === 'hourly'
                            ? (parseFloat(formData.payRate) || 0) * (hoursPerPeriod[payFrequency] || 80)
                            : (parseFloat(formData.payRate) || 0) / (PAY_FREQUENCIES[payFrequency]?.periodsPerYear || 26)
                        )}
                      </span>
                    </div>
                    {formData.payType === 'hourly' && (
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-gray-500">Hourly Rate</span>
                        <span className="font-medium text-amber-600">
                          {formatUSDCurrency(parseFloat(formData.payRate) || 0)}/hr
                        </span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button 
              onClick={handleSave}
              className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
